import { useTranslation } from 'next-i18next';
import { MouseEventHandler, ReactNode } from 'react';
import { Market } from '@bridebook/toolbox/src/gazetteer';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { env } from '@bridebook/web/lib/env';
import {
  LandingPageIds,
  WebflowLandingPages,
  landingPageExists,
} from '@bridebook/web/lib/landing/webflow-page-urls';
import { getCareersUrl } from '@bridebook/web/lib/url-helper';
import FooterAppBadges from './footer-app-badges';

interface IProps {
  market: Market;
  onAppBadgeClick?: MouseEventHandler<HTMLAnchorElement>;
}

export interface IFooterLinkItem {
  title: string;
  href: string;
  openInNewTab?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

interface IFooterColumnData {
  heading: string;
  options?: IFooterLinkItem[];
  customContent?: ReactNode;
}

const FooterTop = ({ market, onAppBadgeClick }: IProps) => {
  const { t } = useTranslation('common');
  const cmsLandingUrl = market.flags.monetized
    ? `${env.CMS_LANDING_URL}/${market.alias || market.country.toLowerCase()}`
    : `${env.CMS_LANDING_URL}/`;

  const aboutContent: IFooterColumnData = {
    heading: t('footer.about.heading'),
    options: [
      ...(landingPageExists(LandingPageIds.aboutUs, market)
        ? [
            {
              title: t('footer.about.aboutUs'),
              href: WebflowLandingPages[LandingPageIds.aboutUs].bridebookPath,
            },
          ]
        : []),
      {
        title: t('footer.about.hiring'),
        href: getCareersUrl('footer'),
        openInNewTab: true,
      },
      ...(landingPageExists(LandingPageIds.pressAndMedia, market)
        ? [
            {
              title: t('footer.about.media'),
              href: WebflowLandingPages[LandingPageIds.pressAndMedia].bridebookPath,
            },
          ]
        : []),
      ...(landingPageExists(LandingPageIds.contactUs, market)
        ? [
            {
              title: t('footer.about.contact'),
              href: WebflowLandingPages[LandingPageIds.contactUs].bridebookPath,
            },
          ]
        : []),
    ],
  };

  const businessContent: IFooterColumnData = {
    heading: t('footer.business.heading'),
    options: [
      {
        title: t('footer.business.bridebook'),
        href: cmsLandingUrl,
        openInNewTab: true,
      },
    ],
  };

  const couplesContent: IFooterColumnData = {
    heading: t('footer.couples.heading'),
    options: [
      ...(landingPageExists(LandingPageIds.weddingPlanningTools, market)
        ? [
            {
              title: t('footer.couples.planning'),
              href: WebflowLandingPages[LandingPageIds.weddingPlanningTools].bridebookPath,
            },
          ]
        : []),
      ...(landingPageExists(LandingPageIds.weddingSuppliers, market)
        ? [
            {
              title: t('footer.couples.supplierDirectory'),
              href: WebflowLandingPages[LandingPageIds.weddingSuppliers].bridebookPath,
            },
          ]
        : []),
      ...(landingPageExists(LandingPageIds.weddingIdeasInspiration, market)
        ? [
            {
              title: t('footer.couples.ideas'),
              href: WebflowLandingPages[LandingPageIds.weddingIdeasInspiration].bridebookPath,
            },
          ]
        : []),
      ...(landingPageExists(LandingPageIds.weddingGiftLists, market)
        ? [
            {
              title: t('footer.couples.giftList'),
              href: WebflowLandingPages[LandingPageIds.weddingGiftLists].bridebookPath,
            },
          ]
        : []),
    ],
  };

  const appBadgesContent: IFooterColumnData = {
    heading: t('footer.getApp.heading'),
    customContent: <FooterAppBadges market={market} onClick={onAppBadgeClick} />,
  };

  const list: IFooterColumnData[] = [
    aboutContent,
    businessContent,
    ...(couplesContent.options?.length ? [couplesContent] : []),
    appBadgesContent,
  ];

  return (
    <div data-name="footer-top" className="bctop">
      <div className="bcgrid">
        {list.map((category) => (
          <div key={`footer-category-${category.heading}`}>
            <h5>{category.heading}</h5>
            {category.customContent ? (
              category.customContent
            ) : category.options ? (
              <ul>
                {category.options.map(({ href, onClick, openInNewTab, title }, i) => (
                  <li key={`footer-option-${i}`}>
                    <Link
                      href={href}
                      onClick={onClick}
                      target={openInNewTab ? '_blank' : undefined}>
                      {title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FooterTop;
